import '../style/global.css';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, Badge, Box, IconButton, List, ListItem, Pagination, Skeleton, Stack, Tooltip, Typography
} from "@mui/material";
import React, { useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import background from "../asset/background-login.png";
import { ScoreGrade, scoreColor } from '../Score';
import { ISentenceScore, ScoreApi } from '../api/ScoreApi';
import { AudioApi } from '../api/AudioApi';
import AppBarContext from "../context/AppBarContext";
import { handleErrorMessage } from "./ErrorDialog";
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import AddSelfStudyBlueIcon from '../asset/icon-add-self-study-blue.png';
import AddSentenceDialog from './AddSentenceDialog';

export default function UserSentenceScore() {
    const { userName } = useParams() as { userName: string };
    const history = useHistory();
    const location = useLocation();
    const { setErrorMessage } = React.useContext(AppBarContext);

    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);
    const [userSentences, setUserSentences] = React.useState<ISentenceScore[]>([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const itemsPerPage = 10;

    React.useEffect(() => {
        const skeletonTimeout = setTimeout(() => setShowSkeleton(true), 300);

        ScoreApi.getSentenceScoresByUser(userName)
            .then((res) => {
                if (res.status >= 300) throw new Error("Failed fetching user's sentence scores");
                setUserSentences(res.data);
            })
            .catch((err) => handleErrorMessage(err, setErrorMessage, history, location))
            .finally(() => setIsLoading(false));

        return () => clearTimeout(skeletonTimeout);
    }, [userName, history, location, setErrorMessage]);

    const paginatedList = userSentences.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const pageCount = Math.ceil(userSentences.length / itemsPerPage);

    const { playAudio } = useAudioPlayer();
    const getAudio = (sentenceScoreId: number) => {
        AudioApi.getSentenceScoreAudio(sentenceScoreId)
            .then(response => {
                if (response.status < 300) return response.data;
                throw new Error("Unable to get sentence audio: " + response.status);
            })
            .then(audioData => playAudio(audioData))
            .catch(err => handleErrorMessage(err, setErrorMessage, history, location));
    };

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedSentence, setSelectedSentence] = useState<{ text: string; id: number } | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedScore, setSelectedScore] = useState<ISentenceScore | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleAddSentenceClick = (sentence: { text: string; id: number }) => {
        setSelectedSentence(sentence);
        setAddDialogOpen(true);
    };

    const handleCloseAddDialog = () => {
        setSelectedSentence(null);
        setAddDialogOpen(false);
    };

    const getScoreStack = (sentenceScore: ISentenceScore) => {
        if (!sentenceScore || !sentenceScore.pinyin_list_spoken || sentenceScore.pinyin_list_spoken.length === 0) {
            return <div>Sentence Incomplete</div>;
        }

        return (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
                {sentenceScore.pinyin_list_spoken.map((pinyinElement, index) => (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        key={index}
                        sx={{
                            backgroundColor: `${scoreColor(sentenceScore.score_grade[index])}22`,
                            borderRadius: 2,
                            px: 0.5,
                            py: 0.5,
                        }}
                    >
                        <Typography sx={{ fontSize: '1rem', mx: 0.5 }}>
                            {pinyinElement}
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', mx: 0.5 }}>
                            {sentenceScore.text[index]}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 300, fontSize: '0.8rem' }}>
                            {Math.round(sentenceScore.score[index])}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        );
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
                width: "100vw",
                minHeight: "100vh",
                padding: 0,
                backgroundImage: `url(${background})`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <Box
                sx={{
                    mt: 4,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "90%",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "16px",
                    border: "1.5px solid rgba(82, 99, 125, 0.2)",
                    position: "relative",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)",
                }}
            >
                {/* Back Button + Title */}
                <Box sx={{ position: "relative", width: "100%", textAlign: "center", mb: 2 }}>
                    <IconButton
                        onClick={() => history.push('/leaderboard', { tab: 0 })}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#002A6E",
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    <Typography variant="h2" sx={{ color: "#002A6E" }}>
                        {decodeURIComponent(userName)}'s Scores
                    </Typography>
                </Box>

                {/* Content */}
                {isLoading && showSkeleton ? (
                    <Stack spacing={2} sx={{ width: "100%", mt: 2 }}>
                        {Array.from({ length: itemsPerPage }).map((_, i) => (
                            <Skeleton
                                key={i}
                                variant="rectangular"
                                height={36}
                                width="100%"
                                sx={{ borderRadius: 2 }}
                            />
                        ))}
                    </Stack>
                ) : (
                <Box sx={{ width: '100%', px: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', }} >
                        <List sx={{ width: '100%' }}>
                            {paginatedList.map((sentenceScore, index) => (
                                <ListItem key={sentenceScore.id} sx={{ mb: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                                        {/* Rank */}
                                        <Box sx={{ flex: '0 0 auto', textAlign: 'center', px: 1 }}>
                                            <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </Typography>
                                        </Box>

                                        {/* Sentence */}
                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                            <Tooltip title={sentenceScore.text}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        textAlign: 'center',
                                                        mx: 2,
                                                        whiteSpace: 'normal',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: '100%',
                                                    }}
                                                >
                                                    {sentenceScore.text}
                                                </Typography>
                                            </Tooltip>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                            {/* Speaker Icon */}
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Tooltip title="Play Pronunciation" arrow>
                                                    <IconButton onClick={() => getAudio(sentenceScore.id)}>
                                                        <img src={SpeakerBlueIcon} style={{ width: '28px', height: '28px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>

                                            {/* Add to Self-Study */}
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Tooltip title="Add to Self-Study" arrow>
                                                    <IconButton
                                                        onClick={() => handleAddSentenceClick({ text: sentenceScore.text, id: sentenceScore.sentence_id })}
                                                    >
                                                        <img src={AddSelfStudyBlueIcon} style={{ width: '24px', height: '24px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {/* Score */}
                                        <Box sx={{ flex: '0 0 10%', textAlign: 'center' }}>
                                            <Badge
                                                badgeContent={Math.round(sentenceScore.score_mean)}
                                                color="secondary"
                                                sx={{
                                                    '.MuiBadge-badge': {
                                                        backgroundColor: `${scoreColor(sentenceScore.score_mean ? sentenceScore.passing_grade : ScoreGrade.Good)}22`,
                                                        fontSize: '1rem',
                                                        fontWeight: 'bold',
                                                        height: 45,
                                                        minWidth: 45,
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        top: '50%',
                                                        right: '50%',
                                                        transform: 'translate(50%, -50%)',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ width: 30, height: 30, borderRadius: '50%', mr: 1 }} />
                                            </Badge>
                                        </Box>

                                        {/* Pronunciation Details */}
                                        {isMobile ? (
                                            <Typography
                                                variant="body2"
                                                onClick={() => {
                                                    setSelectedScore(sentenceScore);
                                                    setOpenDialog(true);
                                                }}
                                                sx={{
                                                    textDecoration: 'underline',
                                                    color: 'primary.main',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Details
                                            </Typography>
                                        ) : (
                                            <Box sx={{ flex: 1, ml: 1 }}>
                                                {getScoreStack(sentenceScore)}
                                            </Box>
                                        )}
                                    </Stack>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Pagination */}
                {!isLoading && pageCount > 1 && (
                    <Pagination
                        count={pageCount}
                        page={currentPage}
                        onChange={(_, value) => setCurrentPage(value)}
                        color="primary"
                        sx={{ mt: 3 }}
                    />
                )}
            </Box>

            {/* Dialog of Sentence Score Stack */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Pronunciation
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenDialog(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedScore && getScoreStack(selectedScore)}
                </DialogContent>
            </Dialog>

            <AddSentenceDialog
                open={addDialogOpen}
                sentence={selectedSentence}
                onClose={handleCloseAddDialog}
            />
        </div>
    );
}
